'use client';

// Dispatcher central pour les blocs de page builder.
// Reçoit un block ({type, config}) et rend le composant correspondant.
// Si le type est inconnu, affiche un placeholder discret en debug only.

import HeroBlock from './HeroBlock';
import RichTextBlock from './RichTextBlock';
import ImageTextBlock from './ImageTextBlock';
import StatsBlock from './StatsBlock';
import CtaBlock from './CtaBlock';
import FeaturesGridBlock from './FeaturesGridBlock';
import ContactBlock from './ContactBlock';
import ColumnsBlock from './ColumnsBlock';
import PackComparisonBlock from './PackComparisonBlock';
import PacksPreviewBlock from './PacksPreviewBlock';
import NouveautesBlock from './NouveautesBlock';
import IconCardsBlock from './IconCardsBlock';
import VideoGalleryBlock from './VideoGalleryBlock';
import VideoSingleBlock from './VideoSingleBlock';  // VIDEO_SEULE_V1

// VIDEO_SEULE_V1 — même type de bloc, deux rendus : mode « Vidéo seule »
// (lecteur intégré) ou galerie (inchangée). `columns: 1` (réglage transitoire
// du 16/09) vaut « Vidéo seule ».
function VideoBlockSwitch({ config = {}, ...rest }) {
  const seule = config.mode === 'single' || config.columns === 1;
  return seule ? <VideoSingleBlock config={config} {...rest} /> : <VideoGalleryBlock config={config} {...rest} />;
}
import ThankYouBlock from './ThankYouBlock';

const REGISTRY = {
  hero: HeroBlock,
  rich_text: RichTextBlock,
  image_text: ImageTextBlock,
  stats: StatsBlock,
  cta: CtaBlock,
  features_grid: FeaturesGridBlock,
  contact_form: ContactBlock,
  columns: ColumnsBlock,
  pack_comparison: PackComparisonBlock,
  packs_preview: PacksPreviewBlock,
  nouveautes: NouveautesBlock,
  icon_cards: IconCardsBlock,
  video_gallery: VideoBlockSwitch,  // 2026-07 : galerie video (capsules) ; VIDEO_SEULE_V1 : lecteur integre
  thankyou: ThankYouBlock,           // 2026-07 : bloc Merci / confirmation
};

export default function BlockRenderer({ block, settings, packs }) {
  if (!block || !block.type) return null;
  const Component = REGISTRY[block.type];
  if (!Component) {
    // Type inconnu — discret en prod, visible en debug
    if (process.env.NEXT_PUBLIC_DEBUG_BLOCKS === '1') {
      return (
        <div style={{
          padding: '20px',
          margin: '12px 24px',
          background: '#fee2e2',
          border: '1px dashed #fca5a5',
          borderRadius: '8px',
          color: '#991b1b',
          fontSize: '13px',
          fontFamily: 'monospace',
        }}>
          Block type inconnu : {block.type}
        </div>
      );
    }
    return null;
  }
  // settings est passe en prop a CHAQUE block. Certains (FeaturesGridBlock)
  // en ont besoin pour acceder a la liste globale features SANS fetch client
  // au mount → SSR HTML contient deja les bonnes features → plus de "page
  // vide au 1er load qui se remplit au refresh".
  return <Component config={block.config || {}} settings={settings} packs={packs} />;
}

/**
 * Rend une LISTE de blocs en sequence. Convenience wrapper.
 * Passe `settings` ET `packs` a chaque block pour que ceux qui ont besoin
 * (features_grid, packs_preview) y aient acces SANS fetch client.
 */
export function BlocksList({ blocks, settings, packs }) {
  if (!blocks || blocks.length === 0) return null;
  // V1.25.1417 — Fix critique d'hydratation (erreur React #329).
  // La cle etait `key={b.id}`. Or une page importee par seed/script peut
  // avoir des blocs SANS `id` → tous les blocs recevaient `key={undefined}`.
  // React 18 ne peut pas apparier une liste d'enfants ayant tous la meme
  // cle pendant l'hydratation → l'hydratation echoue, la page reste figee
  // dans l'etat serveur (dégradé fige, double-clic, pas de survol menu)
  // jusqu'au 1er clic. On garantit ici une cle UNIQUE et STABLE pour chaque
  // bloc : son `id` s'il existe, sinon sa position. Plus aucun doublon de
  // cle possible, quelle que soit l'origine de la page.
  return blocks.map((b, i) => (
    <BlockRenderer
      key={(b && b.id) ? b.id : `block-${i}`}
      block={b}
      settings={settings}
      packs={packs}
    />
  ));
}
